import { useState, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, GizmoHelper, GizmoViewport, PerspectiveCamera } from "@react-three/drei";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  fetchWarehouseTransformById, getStructureVolumes, putWarehouseTransformById,
  uploadWarehouseModel, listWarehouses, type WarehouseTransform,
} from "../../api/rest";
import { WarehouseModel, type ModelBounds } from "../../scene/WarehouseModel";
import { useSiteId } from "../../hooks/useSiteId";
import { WarehousePlanCard } from "../WarehousePlanCard";
import { footprintBbox } from "../planRaster";
import { StructureFootprints } from "./StructureFootprints";
import { frameFor } from "./workbenchFrame";
import css from "./ModelTab.module.css";

const DEFAULT_TRANSFORM: WarehouseTransform = {
  origin_offset: [0, 0, 0],
  rotation_y_rad: 0,
  scale: 1,
};

const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * ADR 313 — the fine channel beside each coarse slider: a typed value plus ▲/▼
 * steppers (also ArrowUp/ArrowDown on the input). The adaptive slider covers the
 * whole site (ADR 310), so one pixel of drag is metres; the last centimetres
 * need digits. Text input, not type=number: operators type decimal commas,
 * which number inputs silently reject.
 */
function ExactField({ label, nudgeUpLabel, nudgeDownLabel, value, step, min, max, decimals, onCommit }: {
  label: string; nudgeUpLabel: string; nudgeDownLabel: string;
  value: number; step: number; min: number; max: number; decimals: number;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const commitText = (text: string) => {
    const v = Number(text.trim().replace(",", "."));
    if (Number.isFinite(v)) onCommit(clamp(v));
  };
  // Step from the committed value, never the draft — half-typed text must not
  // teleport the model. toFixed clips float dust so 0,001-steps stay 0,001.
  const nudge = (dir: 1 | -1) => {
    setDraft(null);
    onCommit(clamp(+(value + dir * step).toFixed(decimals)));
  };
  return (
    <div className={css.exact}>
      <input
        className={css.exactInput}
        inputMode="decimal"
        aria-label={label}
        value={draft ?? value.toFixed(decimals).replace(".", ",")}
        onChange={(e) => { setDraft(e.target.value); commitText(e.target.value); }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") { e.preventDefault(); nudge(1); }
          if (e.key === "ArrowDown") { e.preventDefault(); nudge(-1); }
        }}
      />
      <div className={css.stepBtns}>
        <button type="button" className={css.stepBtn} aria-label={nudgeUpLabel} onClick={() => nudge(1)}>▲</button>
        <button type="button" className={css.stepBtn} aria-label={nudgeDownLabel} onClick={() => nudge(-1)}>▼</button>
      </div>
    </div>
  );
}

/** GLB ingest (moved off the warehouse-list row): upload/replace the model that
 *  the placement controls below then calibrate. */
function ModelFileCard({ siteId, warehouseId, modelKey }: { siteId: string; warehouseId: string; modelKey?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function onPick(file: File) {
    setBusy(true);
    setProgress(0);
    setError(null);
    try {
      await uploadWarehouseModel(siteId, warehouseId, file, setProgress);
      await qc.invalidateQueries({ queryKey: ["warehouses", siteId] });
      await qc.invalidateQueries({ queryKey: ["warehouseModel", siteId, warehouseId] });
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  return (
    <div className={css.form}>
      <span className={css.note}>{modelKey ?? t("warehouse.model.none")}</span>
      <label className={css.fileLabel}>
        {busy ? `${progress.toFixed(0)}%` : t(modelKey ? "warehouse.model.replace" : "warehouse.model.upload")}
        <input type="file" accept=".glb" hidden disabled={busy}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); e.target.value = ""; }} />
      </label>
      {error && <div className={css.err}>{error.message}</div>}
    </div>
  );
}

/**
 * Modelo tab (spec 2026-08-11): the GLB, its placement, and the 2D plan share one
 * tab because all three are reference artifacts for the same warehouse and each
 * needs ingest AND placement — grouping by tool beats scattering them across the
 * old standalone pages. `WarehousePage` already validated `:warehouseId` and
 * renders the shared header, so this tab owns only the tools.
 */
export function ModelTab() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();
  const qc = useQueryClient();

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses", siteId],
    queryFn: () => listWarehouses(siteId),
    enabled: !!siteId,
  });
  const modelKey = warehouses.find((w) => w.id === warehouseId)?.modelKey;

  const { data: saved } = useQuery({
    queryKey: ["warehouseTransform", warehouseId],
    queryFn: () => fetchWarehouseTransformById(siteId, warehouseId),
    enabled: !!warehouseId,
  });

  const [local, setLocal] = useState<WarehouseTransform>(() => saved ?? DEFAULT_TRANSFORM);

  const savedRef = useRef(saved);
  if (saved && saved !== savedRef.current) {
    savedRef.current = saved;
    setLocal(saved);
  }

  const patch = useCallback((p: Partial<WarehouseTransform>) => {
    setLocal((prev) => ({ ...prev, ...p }));
  }, []);

  const mutation = useMutation({
    mutationFn: (t: WarehouseTransform) => putWarehouseTransformById(siteId, warehouseId, t),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouseTransform", warehouseId] }),
  });

  const rotDeg = +(local.rotation_y_rad * (180 / Math.PI)).toFixed(1);

  // ADR 310: the workbench (grid, camera, slider ranges) sizes itself from the
  // loaded GLB's untransformed envelope — the 60 × 40 m constants it replaces were
  // authored for the demo warehouse and put the first real CAD export (543 m wide,
  // needing a ~250 m offset) outside everything: grid, camera and slider range.
  const [bounds, setBounds] = useState<ModelBounds | null>(null);
  const handleBounds = useCallback((b: ModelBounds) => setBounds(b), []);

  // ADR 312: the imported structure is the FIXED reference the model is dragged
  // onto — it lives in the frame live poses arrive in, so it never moves. Shared
  // query key with WarehousePlanCard below: one fetch serves both.
  const { data: volumesData } = useQuery({
    queryKey: ["structure-volumes", siteId, warehouseId],
    queryFn: () => getStructureVolumes(siteId, warehouseId),
    enabled: !!siteId && !!warehouseId,
  });
  const footprints = useMemo(() => {
    const byNode = new Map<string, number[][]>();
    for (const v of volumesData?.volumes ?? []) {
      if (v.nodeId && !byNode.has(v.nodeId) && Array.isArray(v.footprint) && v.footprint.length >= 3) {
        byNode.set(v.nodeId, v.footprint);
      }
    }
    return [...byNode.values()];
  }, [volumesData]);
  const contentBbox = useMemo(() => footprintBbox(footprints), [footprints]);

  const frame = useMemo(() => frameFor(bounds, contentBbox), [bounds, contentBbox]);

  // Seed placement: put the model's footprint centre on the structure's. A seed,
  // not a fit — rotation and fine position stay the operator's, but the two
  // shapes start on top of each other instead of hundreds of metres apart.
  const centerOnStructure = useCallback(() => {
    if (!bounds || !contentBbox) return;
    setLocal((prev) => ({
      ...prev,
      origin_offset: [
        round2((contentBbox.minX + contentBbox.maxX) / 2 - bounds.center[0]),
        prev.origin_offset[1],
        round2((contentBbox.minY + contentBbox.maxY) / 2 - bounds.center[2]),
      ],
    }));
  }, [bounds, contentBbox]);

  return (
    <div className={css.root}>
      <div className={css.workbench}>
        <div className={css.canvas}>
          <Canvas shadows>
            <PerspectiveCamera makeDefault fov={50} position={frame.camera} far={frame.far} />
            <ambientLight intensity={0.6} />
            <directionalLight position={[20, 30, 20]} intensity={1} castShadow />
            <WarehouseModel widthM={frame.floor} heightM={frame.floor}
                            siteId={siteId} warehouseId={warehouseId} transformOverride={local}
                            onBounds={handleBounds} />
            <StructureFootprints footprints={footprints} />
            <gridHelper args={[frame.floor, frame.gridDivisions]}
                        position={[frame.target[0], 0, frame.target[2]]} />
            <OrbitControls makeDefault target={frame.target} />
            <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
              <GizmoViewport />
            </GizmoHelper>
          </Canvas>
        </div>

        <aside className={css.sidebar}>
          <ModelFileCard siteId={siteId} warehouseId={warehouseId} modelKey={modelKey} />

          <div className={css.field}>
            <label className={css.label} htmlFor="offsetX">{t("calibration.offsetX")}</label>
            <ExactField label={t("calibration.offsetXExact")}
              nudgeUpLabel={t("calibration.nudgeUp", { field: t("calibration.offsetX") })}
              nudgeDownLabel={t("calibration.nudgeDown", { field: t("calibration.offsetX") })}
              value={local.origin_offset[0]} step={0.001} decimals={3}
              min={-frame.offsetRange} max={frame.offsetRange}
              onCommit={(v) => patch({ origin_offset: [v, local.origin_offset[1], local.origin_offset[2]] })} />
            <input id="offsetX" type="range" className={css.slider}
              min={-frame.offsetRange} max={frame.offsetRange} step={frame.offsetStep}
              value={local.origin_offset[0]}
              aria-label={t("calibration.offsetX")}
              onChange={(e) => patch({ origin_offset: [+e.target.value, local.origin_offset[1], local.origin_offset[2]] })} />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="offsetZ">{t("calibration.offsetZ")}</label>
            <ExactField label={t("calibration.offsetZExact")}
              nudgeUpLabel={t("calibration.nudgeUp", { field: t("calibration.offsetZ") })}
              nudgeDownLabel={t("calibration.nudgeDown", { field: t("calibration.offsetZ") })}
              value={local.origin_offset[2]} step={0.001} decimals={3}
              min={-frame.offsetRange} max={frame.offsetRange}
              onCommit={(v) => patch({ origin_offset: [local.origin_offset[0], local.origin_offset[1], v] })} />
            <input id="offsetZ" type="range" className={css.slider}
              min={-frame.offsetRange} max={frame.offsetRange} step={frame.offsetStep}
              value={local.origin_offset[2]}
              aria-label={t("calibration.offsetZ")}
              onChange={(e) => patch({ origin_offset: [local.origin_offset[0], local.origin_offset[1], +e.target.value] })} />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="rotY">{t("calibration.rotationY", { deg: rotDeg })}</label>
            <ExactField label={t("calibration.rotationExact")}
              nudgeUpLabel={t("calibration.nudgeUp", { field: t("calibration.rotationExact") })}
              nudgeDownLabel={t("calibration.nudgeDown", { field: t("calibration.rotationExact") })}
              value={local.rotation_y_rad * (180 / Math.PI)} step={0.1} decimals={1}
              min={-180} max={180}
              onCommit={(deg) => patch({ rotation_y_rad: deg * (Math.PI / 180) })} />
            <input id="rotY" type="range" className={css.slider}
              min={-Math.PI} max={Math.PI} step={0.01}
              value={local.rotation_y_rad}
              aria-label={t("calibration.rotationY", { deg: rotDeg })}
              onChange={(e) => patch({ rotation_y_rad: +e.target.value })} />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="scale">{t("calibration.scale")}</label>
            <ExactField label={t("calibration.scaleExact")}
              nudgeUpLabel={t("calibration.nudgeUp", { field: t("calibration.scale") })}
              nudgeDownLabel={t("calibration.nudgeDown", { field: t("calibration.scale") })}
              value={local.scale} step={0.001} decimals={3}
              min={0.1} max={10}
              onCommit={(v) => patch({ scale: v })} />
            <input id="scale" type="range" className={css.slider}
              min={0.1} max={10} step={0.01}
              value={local.scale}
              aria-label={t("calibration.scale")}
              onChange={(e) => patch({ scale: +e.target.value })} />
          </div>

          {/* ADR 312: one click puts the model onto the structure; the sliders
              then fine-tune. Hidden, not disabled, while either half is missing —
              a warehouse with no imported structure has nothing to centre on. */}
          {bounds && contentBbox && (
            <button className={css.resetBtn} style={{ width: "100%" }}
              onClick={centerOnStructure}>
              {t("warehouse.model.centerOnStructure")}
            </button>
          )}

          <div className={css.actions}>
            <button className={css.saveBtn}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(local)}>
              {mutation.isPending ? t("common.saving") : t("common.save")}
            </button>
            <button className={css.resetBtn}
              onClick={() => setLocal(saved ?? DEFAULT_TRANSFORM)}>
              {t("common.reset")}
            </button>
          </div>

          {mutation.isSuccess && <p className={css.statusMsg}>{t("calibration.saved")}</p>}
          {mutation.isError   && <p className={css.statusMsg}>{t("calibration.error")}</p>}
        </aside>
      </div>

      {/* The plan card is a full-width working surface — a preview canvas plus a
          three-field numeric row, written for a page. It sits UNDER the
          viewport/controls row rather than inside the 320px column, where its
          preview and its georeference fields were both squeezed. */}
      <WarehousePlanCard siteId={siteId} warehouseId={warehouseId} />
    </div>
  );
}
